import { Duration } from "luxon";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  type MRT_SortingState,
  useMantineReactTable
} from "mantine-react-table-open";
import { useMemo } from "react";
import { getDefaultMRTOptions } from "../../../../mantine/getDefaultMRTOptions";
import { getDateEndColumn } from "../../../../mantine/mrt/components/columns/getDateEndColumn";
import { getDateStartColumn } from "../../../../mantine/mrt/components/columns/getDateStartColumn";
import type { Generator } from "../../../../utils/backend-types";
import { DATE_TIME_DISPLAY_WITHOUT_SECONDS_SETTINGS } from "../../../../utils/dates";
import { roundedNumericFormatter } from "../../../../utils/RoundedNumberFormat";
import type { AusfallarbeitTimeSeriesResponse } from "../../../RevenueMonitoring/RevenueMonitoring.types";
import { buildAusfallarbeitTableRows } from "./utils/buildAusfallarbeitTableRows";

const DEFAULT_SORTED: MRT_SortingState = [{ id: "startMs", desc: true }];

export interface AusfallarbeitMassnahmeRow {
  id: string;
  startMs: number;
  endMs: number;
  durationMs: number;
  generatorName: string;
  typ: string;
  mengeKwh: number;
}

export interface AusfallarbeitTableProps {
  ausfallarbeitData: AusfallarbeitTimeSeriesResponse | undefined;
  generators: Array<Generator> | undefined;
  isLoading?: boolean;
}

function AusfallarbeitTable({
  ausfallarbeitData,
  generators,
  isLoading
}: AusfallarbeitTableProps) {
  const tableData = useMemo<AusfallarbeitMassnahmeRow[]>(() => {
    if (!ausfallarbeitData || !generators) return [];
    return buildAusfallarbeitTableRows(ausfallarbeitData, generators);
  }, [ausfallarbeitData, generators]);

  const columns = useMemo<Array<MRT_ColumnDef<AusfallarbeitMassnahmeRow>>>(
    () => [
      getDateStartColumn({
        key: "startMs",
        DateTimeFormatOptions: DATE_TIME_DISPLAY_WITHOUT_SECONDS_SETTINGS,
        options: {
          header: "Startzeit",
          accessorFn: (row) => new Date(row.startMs)
        }
      }),
      getDateEndColumn({
        key: "endMs",
        DateTimeFormatOptions: DATE_TIME_DISPLAY_WITHOUT_SECONDS_SETTINGS,
        options: {
          header: "Endzeit",
          accessorFn: (row) => new Date(row.endMs)
        }
      }),
      {
        accessorKey: "durationMs",
        header: "Dauer",
        Cell: ({ cell }) =>
          Duration.fromMillis(cell.getValue<number>()).toFormat("h'h' mm'min'"),
        enableColumnFilter: false
      },
      {
        accessorKey: "generatorName",
        header: "Anlage",
        filterVariant: "multi-select"
      },
      {
        accessorKey: "typ",
        header: "Typ",
        filterVariant: "multi-select"
      },
      {
        accessorKey: "mengeKwh",
        header: "Menge (kWh)",
        Cell: ({ cell }) => roundedNumericFormatter(cell.getValue<number>()),
        filterVariant: "range"
      }
    ],
    []
  );

  const table = useMantineReactTable({
    ...getDefaultMRTOptions<AusfallarbeitMassnahmeRow>({
      emptyRowsFallbackText: "Keine Ausfallarbeit vorhanden.",
      initialState: {
        sorting: DEFAULT_SORTED
      }
    }),
    enableColumnFilters: true,
    enableFacetedValues: true,
    enableRowNumbers: true,
    state: { isLoading },
    columnFilterDisplayMode: "popover",
    columns,
    data: tableData
  });

  return <MantineReactTable table={table} />;
}

export { AusfallarbeitTable };
